import {
  createContext,
  matchPath,
  MiddlewareFunction,
  replace,
  RouterContextProvider,
} from 'react-router';
import { z } from 'zod';
import { crewClaimContext } from '~/app/features/crew/crewClaimMiddleware';
import { appStoreContext } from '~/utils/routing';
import { claimTypeParamSchema } from '~/utils/schemas';

export const routeParamsSchema = z.object({
  claimType: claimTypeParamSchema,
  draftId: z.string(),
  store: z.string(),
  idFromPlatform: z.string(),
});

function getDraftClaimLineItems(
  id: string,
  context: Readonly<RouterContextProvider>,
) {
  const appStore = context.get(appStoreContext);
  const crewClaim = context.get(crewClaimContext);

  const { draftClaimLineItem } = appStore.getState();

  if (draftClaimLineItem?.id === id) {
    return draftClaimLineItem;
  }

  const claimLineItem = crewClaim.claimLineItems.find((c) => c.id === id);

  if (!claimLineItem) {
    throw new Error(`Claim line item with id ${id} not found in crew claim`);
  }

  appStore.dispatch(
    appStore.actions.draftClaimLineItem.startNewClaim(claimLineItem),
  );
  const newDraftClaimLineItem = appStore.getState().draftClaimLineItem;
  if (!newDraftClaimLineItem) {
    // should never happen, but have a runtime guard just in case, given the nullability of the type
    throw new Error('Draft Claim Line Item Not Found After Dispatch');
  }
  return newDraftClaimLineItem;
}

export type DraftClaimLineItem = ReturnType<typeof getDraftClaimLineItems>;
export const draftClaimLineItemContext = createContext<DraftClaimLineItem>();

export const claimLineItemMiddleware: MiddlewareFunction = ({
  context,
  params,
  request,
}) => {
  const { claimType, draftId, store, idFromPlatform } =
    routeParamsSchema.parse(params);

  // the policy enforcement page needs access to state and not be redirected away if the state is invalid
  const isPolicyPage = !!matchPath(
    '/:store/order/:idFromPlatform/:claimType/:draftId/follow-up',
    new URL(request.url).pathname,
  );

  try {
    const draftClaimLineItem = getDraftClaimLineItems(draftId, context);

    if (draftClaimLineItem.invalid && !isPolicyPage) {
      throw new Error('Invalid claim line item');
    }

    context.set(draftClaimLineItemContext, draftClaimLineItem);
  } catch {
    const appStore = context.get(appStoreContext);

    appStore.dispatch(appStore.actions.draftClaimLineItem.clear());

    throw replace(
      `/${store}/order/${idFromPlatform}/${claimType.toLowerCase()}`,
    );
  }
};
